import React, { useRef, useEffect } from "react";
import * as THREE from "three";

export default function STLPreview({ triangles, width = 300, height = 250 }) {
  const mountRef = useRef(null);
  const rendererRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current || !triangles || triangles.length === 0) return;

    const container = mountRef.current;
    const w = width;
    const h = height;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xF4F2EC);

    // Camera
    const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 1000);

    // Renderer
    if (rendererRef.current) {
      rendererRef.current.dispose();
      if (rendererRef.current.domElement.parentNode) {
        rendererRef.current.domElement.parentNode.removeChild(rendererRef.current.domElement);
      }
    }
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Build geometry from triangles
    const positions = [];
    const normals = [];
    for (const tri of triangles) {
      for (const v of tri.v) {
        positions.push(v[0], v[1], v[2]);
      }
      for (let i = 0; i < 3; i++) {
        normals.push(tri.n[0], tri.n[1], tri.n[2]);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));

    // Materials — base in dark, raised parts in bronze
    const material = new THREE.MeshPhongMaterial({
      color: 0xC8956C,
      specular: 0x444444,
      shininess: 40,
      flatShading: true,
    });

    const mesh = new THREE.Mesh(geometry, material);

    // Center: offset mesh inside a pivot group so rotation is around center
    geometry.computeBoundingBox();
    const box = geometry.boundingBox;
    const center = new THREE.Vector3();
    box.getCenter(center);
    mesh.position.set(-center.x, -center.y, -center.z);

    const pivot = new THREE.Group();
    pivot.add(mesh);
    scene.add(pivot);

    const size = new THREE.Vector3();
    box.getSize(size);
    const maxDim = Math.max(size.x, size.y, size.z);
    camera.position.set(0, -maxDim * 0.8, maxDim * 1.2);
    camera.lookAt(0, 0, 0);

    // Lights
    const ambient = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambient);
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight.position.set(maxDim, -maxDim, maxDim * 2);
    scene.add(dirLight);
    const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    dirLight2.position.set(-maxDim, maxDim, maxDim);
    scene.add(dirLight2);

    // Auto-rotate
    let angle = 0;
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      angle += 0.005;
      pivot.rotation.z = angle;
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
    };
  }, [triangles, width, height]);

  if (!triangles || triangles.length === 0) {
    return (
      <div style={{ width, height, display: "flex", alignItems: "center", justifyContent: "center", background: "#FFFFFF", borderRadius: 0, border: "2px solid #0A0A0F", color: "rgba(10,10,15,0.58)", fontSize: 14 }}>
        Configure STL settings to preview
      </div>
    );
  }

  return (
    <div ref={mountRef} style={{ width, height, borderRadius: 0, overflow: "hidden", border: "2px solid #0A0A0F" }} />
  );
}
